/**
 * <s-wonder-product>: drives the Seven Wonders story product pages.
 * - Marks itself [data-wonder-ready]; each [data-reveal] gets [data-in-view] the first time it scrolls in (CSS reveals it).
 * - [data-words]: splits a heading into words that light up one by one as it scrolls through the viewport.
 * - [data-zoom-stage]: sets --z (2.6 → 1) on its img and --p (0 → 1) on the stage while it scrolls past.
 * - [data-drift]: outlined name slides sideways with scroll.
 * - Gallery: [data-gallery-track] + [data-gallery-thumb] buttons stay in sync.
 * - Size pills (input[data-size-pill]) fill the form's hidden id, enable Add to cart / Buy it now and update the sticky bar.
 * - [data-wonder-sticky]: mobile bar shown while the buy form is off screen; fires `sticky-atc:change` so s-dock steps aside.
 *   Its button adds to cart when a size is chosen, otherwise scrolls to the sizes and highlights them.
 * Motion (word light-up, zoom, drift) is skipped for shoppers who prefer reduced motion.
 */
class SWonderProduct extends HTMLElement {
  #ac;
  #observers = [];
  #frame = 0;
  #calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const reveal = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute('data-in-view', '');
          reveal.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.1 }
    );
    this.querySelectorAll('[data-reveal]').forEach((el) => reveal.observe(el));
    this.#observers.push(reveal);
    this.setAttribute('data-wonder-ready', '');

    if (!this.#calm) this.querySelectorAll('[data-words]').forEach((el) => this.#splitWords(el));

    this.addEventListener('change', (event) => this.#onSizeChange(event), opts);
    this.#wireGallery(opts);
    this.#wireSticky(opts);

    if (!this.#calm) {
      window.addEventListener('scroll', () => this.#schedule(), { ...opts, passive: true });
      window.addEventListener('resize', () => this.#schedule(), { ...opts, passive: true });
      this.#tick();
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observers.forEach((o) => o.disconnect());
    cancelAnimationFrame(this.#frame);
  }

  #splitWords(el) {
    const target = el.querySelector('h1, h2, h3, p') || el;
    const words = target.textContent.trim().split(/\s+/);
    target.setAttribute('aria-label', target.textContent.trim());
    target.textContent = '';
    words.forEach((word, i) => {
      const span = document.createElement('span');
      span.textContent = word;
      span.setAttribute('aria-hidden', 'true');
      span.dataset.word = '';
      span.style.opacity = '0.15';
      span.style.transition = 'opacity 0.4s ease';
      target.append(span);
      if (i < words.length - 1) target.append(' ');
    });
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#tick());
  }

  #tick() {
    const vh = window.innerHeight;

    for (const el of this.querySelectorAll('[data-words]')) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < -vh || rect.top > vh * 2) continue;
      const progress = Math.min(1, Math.max(0, (vh * 0.85 - rect.top) / (vh * 0.55 + rect.height)));
      const spans = el.querySelectorAll('[data-word]');
      const lit = Math.round(progress * spans.length);
      spans.forEach((span, i) => (span.style.opacity = i < lit ? '1' : '0.15'));
    }

    for (const stage of this.querySelectorAll('[data-zoom-stage]')) {
      const rect = stage.getBoundingClientRect();
      const travel = rect.height - vh;
      if (travel <= 0) continue;
      const p = Math.min(1, Math.max(0, -rect.top / travel));
      const eased = 1 - Math.pow(1 - p, 2);
      const img = stage.querySelector('img');
      if (img) img.style.setProperty('--z', (2.6 - 1.6 * eased).toFixed(3));
      stage.style.setProperty('--p', Math.min(1, Math.max(0, (p - 0.65) / 0.3)).toFixed(3));
    }

    for (const word of this.querySelectorAll('[data-drift]')) {
      const host = word.closest('section') || word;
      const rect = host.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) continue;
      const progress = (vh - rect.top) / (vh + rect.height);
      word.style.transform = `translate3d(${((0.5 - progress) * 16).toFixed(2)}vw, 0, 0)`;
    }
  }

  #wireGallery(opts) {
    const track = this.querySelector('[data-gallery-track]');
    const thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!track || !thumbs.length) return;
    thumbs.forEach((thumb) =>
      thumb.addEventListener(
        'click',
        () => {
          const slide = track.children[Number(thumb.dataset.galleryThumb)];
          if (!slide) return;
          const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
          track.scrollTo({ left: rtl * (slide.offsetLeft - track.offsetLeft), behavior: this.#calm ? 'auto' : 'smooth' });
        },
        opts
      )
    );
    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const index = Math.round(Math.abs(track.scrollLeft) / Math.max(1, track.clientWidth));
          thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
        });
      },
      { ...opts, passive: true }
    );
  }

  get #form() {
    return this.querySelector('#buy form');
  }

  #onSizeChange(event) {
    const pill = event.target;
    if (!(pill instanceof HTMLInputElement) || !pill.matches('[data-size-pill]')) return;
    const form = pill.form;
    if (!form) return;
    const input = form.elements.namedItem('id');
    if (input instanceof HTMLInputElement) input.value = pill.value;
    form.querySelectorAll('[data-wonder-add], [data-wonder-buy-now]').forEach((button) => {
      button.disabled = false;
      button.removeAttribute('aria-describedby');
    });
    const hint = form.querySelector('[data-wonder-hint]');
    if (hint) hint.hidden = true;
    form.querySelector('[data-size-fieldset]')?.removeAttribute('data-nudge');

    const price = this.querySelector('[data-wonder-price]');
    if (price && pill.dataset.price) price.textContent = pill.dataset.price;
    const stickyPrice = this.querySelector('[data-sticky-price]');
    if (stickyPrice && pill.dataset.price) stickyPrice.textContent = `${pill.dataset.sizeLabel} · ${pill.dataset.price}`;
    const action = this.querySelector('[data-sticky-action]');
    if (action) action.textContent = action.dataset.labelAdd;
  }

  #wireSticky(opts) {
    const bar = this.querySelector('[data-wonder-sticky]');
    const buy = this.querySelector('#buy');
    if (!bar || !buy) return;
    let visible = false;
    const setVisible = (next) => {
      if (next === visible) return;
      visible = next;
      bar.toggleAttribute('data-hidden', !next);
      bar.inert = !next;
      document.dispatchEvent(new CustomEvent('sticky-atc:change', { detail: { visible: next } }));
    };
    const observer = new IntersectionObserver(([entry]) => {
      const pastBuy = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      const footer = document.querySelector('footer');
      const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      setVisible(pastBuy && !footerVisible);
    });
    observer.observe(buy.querySelector('form') || buy);
    this.#observers.push(observer);
    const footer = document.querySelector('footer');
    if (footer) {
      const footerObserver = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) setVisible(false);
        else {
          const form = buy.querySelector('form') || buy;
          setVisible(form.getBoundingClientRect().bottom < 0);
        }
      });
      footerObserver.observe(footer);
      this.#observers.push(footerObserver);
    }

    bar.querySelector('[data-sticky-action]')?.addEventListener(
      'click',
      () => {
        const form = this.#form;
        if (!form) return;
        const input = form.elements.namedItem('id');
        const add = form.querySelector('[data-wonder-add]');
        if (input instanceof HTMLInputElement && input.value && add) {
          form.requestSubmit(add);
          return;
        }
        const fieldset = form.querySelector('[data-size-fieldset]');
        (fieldset || form).scrollIntoView({ behavior: this.#calm ? 'auto' : 'smooth', block: 'center' });
        fieldset?.setAttribute('data-nudge', '');
        window.setTimeout(() => fieldset?.querySelector('input:not(:disabled)')?.focus({ preventScroll: true }), 500);
      },
      opts
    );
  }
}

if (!customElements.get('s-wonder-product')) customElements.define('s-wonder-product', SWonderProduct);
